import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { CategoriaService } from '../../../shared/services/apis/produto/categoria.service';
import { CategoriaStruct } from '../../../shared/services/structs/produto/categoria.struct';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { ReturnStruct } from '../../../shared/services/structs/return.struct';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { FooterActionComponent } from '../../../shared/components/footer-action/footer-action.component';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';

/** Local editable tree node (in-memory, not persisted until Save) */
export interface CategoriaNode {
    idCategoria: number | null;
    nome: string;
    originalNome?: string;
    filhos: CategoriaNode[];
    isEditing: boolean;
    isNew: boolean;
    isExpanded: boolean;
    _tempId: number;
}

let _nextTempId = 1;

@Component({
    selector: 'app-categoria-cadastro',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatDialogModule,
        LoadingBoxComponent,
        FooterActionComponent,
        HeaderComponent
    ],
    templateUrl: './categoria-cadastro.component.html',
    styleUrl: './categoria-cadastro.component.css'
})
export class CategoriaCadastroComponent implements OnInit {

    public moduloEnum = ModuloEnum.Cadastros;
    public funcionalidadeEnum = FuncionalidadeEnum.Cadastros_Categorias;

    public isEditMode: boolean = false;
    public isLoading: boolean = false;
    public idCategoria: number | null = null;
    public isSaving: boolean = false;

    /** The root category ID (the top ancestor in edit mode) */
    public idRaiz: number | null = null;

    /** The specific node ID being edited (for highlighting) */
    public editingNodeId: number | null = null;

    /** The full tree of categories (root nodes at the top level) */
    public treeNodes: CategoriaNode[] = [];

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private categoriaService: CategoriaService,
        private alertService: AlertService,
        private dialog: MatDialog
    ) { }

    ngOnInit(): void {
        const paramId = this.route.snapshot.paramMap.get('idCategoria');
        if (paramId) {
            this.idCategoria = Number(paramId);
            this.editingNodeId = this.idCategoria;
            this.isEditMode = true;
            this.loadData(this.idCategoria);
        } else {
            // In create mode, start with one empty root node immediately
            this.adicionarSubcategoriaRaiz();
        }
    }

    private loadData(id: number): void {
        this.isLoading = true;
        this.categoriaService.getByIdComFamilia(id).subscribe({
            next: (res) => {
                this.isLoading = false;
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription || 'Erro ao carregar categoria.', AlertType.error);
                    this.router.navigate(['/cadastros/categorias']);
                    return;
                }
                if (!res.categoria) {
                    this.alertService.show('Erro', 'Categoria não encontrada.', AlertType.error);
                    this.router.navigate(['/cadastros/categorias']);
                    return;
                }
                // The API returns the root ancestor — make it the top tree node
                this.idRaiz = res.categoria.idCategoria;
                const rootNode = this.mapToEditableNode(res.categoria);
                this.treeNodes = [rootNode];
            },
            error: () => {
                this.isLoading = false;
                this.alertService.show('Erro', 'Não foi possível carregar a categoria.', AlertType.error);
                this.router.navigate(['/cadastros/categorias']);
            }
        });
    }

    /** Convert a single API struct to an editable node (recursive) */
    private mapToEditableNode(item: CategoriaStruct): CategoriaNode {
        return {
            idCategoria: item.idCategoria,
            nome: item.nome,
            filhos: (item.filhos || []).map(f => this.mapToEditableNode(f)),
            isEditing: false,
            isNew: false,
            isExpanded: true,
            _tempId: _nextTempId++
        };
    }

    // ─── Tree Actions ────────────────────────────────────────────

    /** Add a root-level category */
    adicionarSubcategoriaRaiz(): void {
        const newNode: CategoriaNode = {
            idCategoria: null,
            nome: '',
            originalNome: '',
            filhos: [],
            isEditing: true,
            isNew: true,
            isExpanded: true,
            _tempId: _nextTempId++
        };
        this.treeNodes.unshift(newNode);
    }

    /** Add a subcategory to a specific node */
    adicionarSubcategoria(parent: CategoriaNode): void {
        const newNode: CategoriaNode = {
            idCategoria: null,
            nome: '',
            originalNome: '',
            filhos: [],
            isEditing: true,
            isNew: true,
            isExpanded: true,
            _tempId: _nextTempId++
        };
        parent.filhos.unshift(newNode);
        parent.isExpanded = true;
    }

    /** Toggle expand/collapse */
    toggleExpand(node: CategoriaNode): void {
        node.isExpanded = !node.isExpanded;
    }

    /** Toggle inline editing on a node */
    editarNode(node: CategoriaNode): void {
        node.originalNome = node.nome;
        node.isEditing = true;
    }

    /** Confirm inline editing */
    confirmarEdicao(node: CategoriaNode): void {
        if (!node.isEditing) return;

        const nomeTrimmed = node.nome.trim();
        if (!nomeTrimmed) return;

        // Validation: Check for duplicates at the same level
        const siblings = this.findNodeSiblings(this.treeNodes, node);
        const duplicate = siblings.find(s => s._tempId !== node._tempId && s.nome.trim().toLowerCase() === nomeTrimmed.toLowerCase());

        if (duplicate) {
            this.alertService.show('Nome Duplicado', `Já existe uma categoria chamada "${nomeTrimmed}" neste nível.`, AlertType.warning);
            return;
        }

        node.isEditing = false;
        node.isNew = false;
    }

    /** Cancel editing — removes the node if it was new (unless it's the root) */
    cancelarEdicao(node: CategoriaNode): void {
        const isRoot = this.treeNodes[0] === node;

        if (node.isNew && !isRoot) {
            this.removerNodeDaArvore(this.treeNodes, node);
        } else {
            // Restore original name if it was being edited
            if (node.originalNome !== undefined) {
                node.nome = node.originalNome;
            }
            node.isEditing = false;
        }
    }

    /** Handle Enter key on inline input */
    onKeydown(event: KeyboardEvent, node: CategoriaNode): void {
        if (event.key === 'Enter') {
            (event.target as HTMLElement).blur();
        } else if (event.key === 'Escape') {
            this.cancelarEdicao(node);
        }
    }

    /** Delete a node with confirmation */
    excluirNode(node: CategoriaNode): void {
        const hasChildren = node.filhos.length > 0;

        const description = hasChildren
            ? `Deseja excluir a categoria "${node.nome}"? Atenção: todas as suas subcategorias também serão excluídas.`
            : `Deseja realmente excluir a categoria "${node.nome}"?`;

        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            data: {
                title: 'Excluir Categoria',
                description: description,
                buttonConfirm: 'Excluir',
                buttonClose: 'Cancelar'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.confirm) {
                this.removerNodeDaArvore(this.treeNodes, node);
            }
        });
    }

    /** Recursively find the array containing a specific node */
    private findNodeSiblings(list: CategoriaNode[], target: CategoriaNode): CategoriaNode[] {
        if (list.some(n => n._tempId === target._tempId)) {
            return list;
        }
        for (const item of list) {
            const result = this.findNodeSiblings(item.filhos, target);
            if (result.length > 0) return result;
        }
        return [];
    }

    /** Recursively check for duplicates at any level */
    private hasHierarchicalDuplicates(nodes: CategoriaNode[]): boolean {
        const names = nodes.map(n => n.nome.trim().toLowerCase());
        const hasDup = names.some((name, index) => names.indexOf(name) !== index);
        if (hasDup) return true;

        for (const node of nodes) {
            if (this.hasHierarchicalDuplicates(node.filhos)) return true;
        }
        return false;
    }

    /** Recursively remove a node from the tree */
    private removerNodeDaArvore(list: CategoriaNode[], target: CategoriaNode): boolean {
        const index = list.findIndex(n => n._tempId === target._tempId);
        if (index >= 0) {
            list.splice(index, 1);
            return true;
        }
        for (const item of list) {
            if (this.removerNodeDaArvore(item.filhos, target)) {
                return true;
            }
        }
        return false;
    }

    /** Check if a node should be highlighted */
    isHighlighted(node: CategoriaNode): boolean {
        // Highlight if it's the specific node being searched/edited from route
        if (node.idCategoria === this.editingNodeId && this.editingNodeId !== null) return true;
        // Also highlight if it's currently in inline edit mode
        if (node.isEditing) return true;
        return false;
    }

    // ─── Save ────────────────────────────────────────────────────

    salvar(): void {
        if (this.isSaving) return;

        // Validate at least one root node with a name
        const root = this.treeNodes[0];
        if (!root || root.nome.trim() === '') {
            this.alertService.show('Atenção', 'Adicione pelo menos uma categoria.', AlertType.warning);
            return;
        }

        // Validate no empty names in the tree (prevents silent data loss)
        if (this.hasEmptyNames(this.treeNodes)) {
            this.alertService.show('Atenção', 'Existem categorias sem nome. Preencha ou remova antes de salvar.', AlertType.warning);
            return;
        }

        // Final recursive validation for any duplicates at any level
        if (this.hasHierarchicalDuplicates(this.treeNodes)) {
            this.alertService.show('Atenção', 'Existem categorias com nomes duplicados no mesmo nível.', AlertType.warning);
            return;
        }

        this.isSaving = true;

        if (this.isEditMode && this.idRaiz) {
            // In edit mode, include idCategoria on every node
            const payload = {
                idCategoria: root.idCategoria,
                nome: root.nome,
                filhos: this.buildPayloadTree(root.filhos)
            };
            this.categoriaService.update(this.idRaiz, payload).subscribe({
                next: (res) => this.handleSuccess(res),
                error: () => this.handleError()
            });
        } else {
            // In create mode, save the single root node
            const root = this.treeNodes[0];
            const payload = {
                nome: root.nome,
                filhos: this.buildPayloadTree(root.filhos)
            };
            this.categoriaService.create(payload).subscribe({
                next: (res) => this.handleSuccess(res),
                error: () => this.handleError()
            });
        }
    }

    /** Build the payload tree for the API */
    private buildPayloadTree(nodes: CategoriaNode[]): any[] {
        return nodes.map(n => ({
            idCategoria: n.idCategoria,
            nome: n.nome.trim(),
            filhos: this.buildPayloadTree(n.filhos)
        }));
    }

    /** Recursively check for empty names in the tree */
    private hasEmptyNames(nodes: CategoriaNode[]): boolean {
        for (const node of nodes) {
            if (!node.nome.trim()) return true;
            if (this.hasEmptyNames(node.filhos)) return true;
        }
        return false;
    }

    private handleSuccess(res: ReturnStruct): void {
        this.isSaving = false;
        if (res.isError) {
            this.alertService.show('Erro', res.errorDescription, AlertType.error);
            return;
        }
        this.alertService.show('Sucesso', 'Categoria salva com sucesso.', AlertType.success);
        this.router.navigate(['/cadastros/categorias']);
    }

    private handleError(): void {
        this.isSaving = false;
        this.alertService.show('Erro', 'Ocorreu um erro ao salvar.', AlertType.error);
    }

    cancelar(): void {
        this.router.navigate(['/cadastros/categorias']);
    }
}
